import { ComponentType, useCallback, useRef } from 'react';
import noop from 'lodash/noop';
import { useNavigate } from 'react-router-dom';
import * as authenticationService from '../../data-layer/authentication/service';
import { subscribeToBusinessAsync } from '../../data-layer/business/actions';
import {
    removeCollectionAsync,
    removeSavedCollectionAsync,
    saveCollectionAsync,
} from '../../data-layer/collections/actions';
import { DEFAULT_COLLECTION_ID } from '../../data-layer/collections/service';
import {
    bookmarkPostAsync,
    removePostsAsync,
    removeSavedCollectionFromPostAsync,
    saveCollectionFromPostAsync,
    shareCollectionAsync,
    sharePostAsync,
    updatePostReactionAsync,
    updatePostStatusAsync,
} from '../../data-layer/posts/actions';
import { PostStatus } from '../../data-layer/posts/constants';
import * as postsService from '../../data-layer/posts/service';
import { PostModel } from '../../data-layer/posts/types';
import { useDispatch } from '../../data-layer/useDispatch';
import { usePostViewEvent } from '../../hooks/usePostViewEvent';
import { toPostUI } from '../../mappers/posts/toPostUI';
import { confirmAlert } from '../../ui/feedback/Alert';
import { makeMediaParams } from '../../ui/posts/PostDetailsCard/utils';
import { ClickoutTarget, PostUI, ProductItemUI } from '../../ui/posts/types';
import { contentActionsService } from '../../utils/ContentActionsService/ContentActionsService';
import { canEditBusiness, isBlogger, isBloggerBusiness } from '../../utils/accessService';
import {
    analyticsService,
    EventSource,
    trackViewPostBoost,
    VideoStates,
} from '../../utils/analyticsService';
import { trackEcommerceEvent } from '../../utils/ecommerce';
import { History } from '../../utils/history';
import {
    getPageNameByEventSource,
    ymTrackClickout,
    ymTrackContextMenuOpen,
    ymTrackFeedScroll,
    ymTrackOpenCatalogFromPost,
    ymTrackPostDeleteClick,
    ymTrackPostEditClick,
    ymTrackPostExpand,
    ymTrackPostInGridBusinessClick,
    ymTrackPostInGridClick,
    ymTrackPostInGridKebabBusinessClick,
    ymTrackPostInGridKebabClick,
    ymTrackPostInGridKebabReport,
    ymTrackPostInGridKebabShare,
    ymTrackPostInGridRowShown,
    ymTrackPostInGridScroll,
    ymTrackPostInGridVideoPlay,
    ymTrackPostShare,
    ymTrackPostStatusClick,
    ymTrackProductClickOut,
    ymTrackProductsUnroll,
    ymTrackSawFifteenthClick,
    ymTrackSawFifthClick,
    ymTrackSawTenthClick,
    ymTrackSearchGoToPost,
} from '../../utils/metrika';
import { useOpenDialog, useSwitchDialog } from '../ModalRoute';
import { DIALOGS } from '../constants';
import { useCurrentUser } from '../users/useCurrentUser';

type OnShareParams = { mediaId: string };

type OnClickOutClickParams = { mediaId: string };

type OnReactionParams = { postNumber: number; reactionState: PostModel['reactionState'] };

type OnSubscribeParams = { postNumber: number };

type OnSlideChangeParams = {
    mediaId: string;
    mediaIndex: number;
};

type OnExpandParams = {
    mediaId: string;
    mediaIndex: number;
};

type OnVisibilityChangeParams = {
    mediaId: string;
    isInViewport: boolean;
    postNumber: number;
    isGrid?: boolean;
};

type OnMuteChangeParams = {
    mediaId: string;
    mute: boolean;
};

type OnVideoStateChangeParams = {
    mediaId: string;
};

type OnGridCardClickParams = {
    postNumber: number;
};

export interface WithPostCallbacksProps {
    onEdit?: VoidFunction;
    onRemove?: VoidFunction;
    onReport?: VoidFunction;
    onDraft?: VoidFunction;
    onPublish?: VoidFunction;
    onBookmark?: VoidFunction;
    onProductButtonClick: VoidFunction;
    onPostTextProductsClick: (product: ProductItemUI) => void;
    onPostTextCheckoutClick: (product: ProductItemUI) => void;
    onPostTextCatalogClick: VoidFunction;
    onPostTextClickoutClick: VoidFunction;
    onBusinessPage?: (isKebab: boolean) => void;
    onShare?: (params: OnShareParams) => void;
    onShareCollection?: (params: OnShareParams) => void;
    onSaveCollection?: VoidFunction;
    onRemoveSavedCollection?: VoidFunction;
    onExpand?: (params: OnExpandParams) => void;
    onReaction: (params: OnReactionParams) => void;
    onSubscribe?: (params: OnSubscribeParams) => void;
    onSlideChange: (params: OnSlideChangeParams) => void;
    onClickOutClick: (params: OnClickOutClickParams) => void;
    onVisibilityChange: (params: OnVisibilityChangeParams) => void;
    onVideoMuteChange: (params: OnMuteChangeParams) => void;
    onVideoEnd: (params: OnVideoStateChangeParams) => void;
    onVideoStart: (params: OnVideoStateChangeParams) => void;
    onVideoPlay: (params: OnVideoStateChangeParams) => void;
    onVideoPause: (params: OnVideoStateChangeParams) => void;
    onStatusBadgeClick: VoidFunction;
    onGridRowShown?: VoidFunction;
    moveToCollection: VoidFunction;
    onEditPostCollection: VoidFunction;
    onHover: VoidFunction;
    onHoverCancel: VoidFunction;
    onGridCardClick: (params: OnGridCardClickParams) => void;
    onCollectionGridCardClick: (params: OnGridCardClickParams) => void;
}

type ExternalParams = {
    post: PostModel;
    index: number;
    onExpand?: VoidFunction;
    onRemove?: VoidFunction;
    fromCollectionId?: string;
    fromBusinessId?: string;
    fromManagement?: boolean;
    gridColumns?: number;
    onReport?: VoidFunction;
    hasLogPostViewEvent?: boolean;
};

export const withPostCallbacks =
    <T extends Object>(
        BaseComponent: ComponentType<T & WithPostCallbacksProps>,
        eventSource: EventSource,
    ): ComponentType<
        Omit<
            Pick<T, Exclude<keyof T, keyof WithPostCallbacksProps>>,
            'post' | 'onExpand' | 'onRemove'
        > &
            ExternalParams
    > =>
    ({
        post,
        fromCollectionId,
        fromBusinessId,
        fromManagement,
        index,
        gridColumns: columns,
        hasLogPostViewEvent,
        ...other
    }) => {
        const dispatch = useDispatch();
        const currentUser = useCurrentUser();
        const openDialog = useOpenDialog();
        const switchDialog = useSwitchDialog(false);
        const navigate = useNavigate();

        const innerRef = useRef<HTMLElement | null>(null);

        const { sendPostViewEvent } = usePostViewEvent({
            ...makeMediaParams(post, 0),
            eventSource,
            postId: post.id,
            collectionId: fromCollectionId,
            djRequestId: post.djRequestId,
            logContext: post.logContext,
            postNumber: index,
            products: post.products.items.map((product) => product.id),
            isBoosted: Boolean(post.adsData),
            columns,
            containerRef: innerRef,
            disable: !hasLogPostViewEvent,
        });

        const onShare = useCallback(
            (params: OnShareParams) => {
                if (post.status === PostStatus.MODERATION || post.status === PostStatus.DECLINED) {
                    openDialog(DIALOGS.POST_STATUS, { post: post });

                    return;
                }
                dispatch(sharePostAsync(post.id));
                analyticsService.trackShare({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    logContext: post.logContext,
                    mediaId: params.mediaId,
                    eventSource,
                    collectionId: fromCollectionId,
                    columns,
                });

                if (eventSource === EventSource.POST) {
                    ymTrackPostInGridKebabShare({
                        page: getPageNameByEventSource(eventSource),
                        userLogin: currentUser?.login,
                        collectionId: fromCollectionId,
                        own: post.ownersData?.isOwner,
                    });
                } else {
                    ymTrackPostShare({ own: post.ownersData?.isOwner });
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [post, dispatch, fromCollectionId, columns],
        );

        const onShareCollection = useCallback(
            (params: OnShareParams) => {
                dispatch(shareCollectionAsync(post.shareUrl));
                analyticsService.trackShare({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    logContext: post.logContext,
                    mediaId: params?.mediaId,
                    eventSource,
                    collectionId: post.collectionData?.id,
                    columns,
                });

                ymTrackPostShare({ own: post.ownersData?.isOwner });
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [post, dispatch, fromCollectionId, columns],
        );

        const moveToSavedCollections = useCallback(() => {
            if (isBlogger(currentUser?.role)) {
                navigate(
                    `/profile/${currentUser?.profile?.publicId}/collections?collectionType=saved`,
                );
            } else {
                navigate('/profile?collectionType=saved');
            }
        }, [currentUser?.profile?.publicId, currentUser?.role, navigate]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
        const onSaveCollection = useCallback(
            authenticationService.protect(async () => {
                if (!post.collectionData?.id) {
                    return;
                }
                await dispatch(saveCollectionAsync(post.collectionData.id, moveToSavedCollections));
                await dispatch(saveCollectionFromPostAsync(post.id));
            }),
            [dispatch, authenticationService.protect, saveCollectionAsync],
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
        const onRemoveSavedCollection = useCallback(
            authenticationService.protect(() => {
                if (!post.collectionData?.id) {
                    return;
                }
                dispatch(removeSavedCollectionAsync(post.collectionData.id));
                dispatch(removeSavedCollectionFromPostAsync(post.id));
            }),
            [dispatch, authenticationService.protect, removeSavedCollectionAsync],
        );

        const onReaction = useCallback(
            (params: {
                postNumber: number;
                reactionState: PostUI['reactionState'];
                preventReactionToggle?: boolean;
            }) => {
                dispatch(
                    updatePostReactionAsync({
                        postId: post.id,
                        postNumber: params.postNumber,
                        reactionState: params.reactionState,
                        collectionId: fromCollectionId,
                        eventSource,
                        preventReactionToggle: params.preventReactionToggle,
                    }),
                );
                trackEcommerceEvent({
                    post,
                    trackEcommerceView: true,
                });
            },
            [post, dispatch, fromCollectionId],
        );

        const onReport = useCallback(() => {
            switchDialog(DIALOGS.REPORT_DIALOG, {
                targetId: post.id,
                targetType: post.postType,
            });

            if (eventSource === EventSource.POST) {
                ymTrackPostInGridKebabReport({
                    page: getPageNameByEventSource(eventSource),
                    userLogin: currentUser?.login,
                    collectionId: fromCollectionId,
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [post, openDialog, eventSource, currentUser, fromCollectionId]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
        const onSubscribe = useCallback(
            authenticationService.protect((params: { postNumber: number }) => {
                if (post.business) {
                    dispatch(
                        subscribeToBusinessAsync(
                            post.business,
                            eventSource,
                            params.postNumber,
                            post.id,
                            fromCollectionId,
                        ),
                    );
                    trackEcommerceEvent({
                        post,
                        trackEcommerceView: true,
                    });
                }
            }),
            [post, dispatch, fromCollectionId],
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
        const onBookmark = useCallback(
            authenticationService.protect(async () => {
                if (post.saved && fromCollectionId && fromCollectionId !== DEFAULT_COLLECTION_ID) {
                    return openDialog(DIALOGS.DELETE_FROM_COLLECTION_DIALOG_NAME, {
                        postId: post.id,
                        collectionId: fromCollectionId,
                    });
                }
                const isSaved = await dispatch(bookmarkPostAsync(post.id));

                if (isSaved) {
                    openDialog(DIALOGS.SAVE_TO_COLLECTION_DIALOG_NAME, { postId: post.id });
                    contentActionsService.trackSaveToCollectionDialogOpen({
                        id: post.id,
                        screen: eventSource,
                    });
                }

                trackEcommerceEvent({
                    post,
                    trackEcommerceView: true,
                });
            }),
            [post, dispatch, openDialog, fromCollectionId],
        );

        const onClickOutClick = useCallback(
            (params: OnClickOutClickParams) => {
                analyticsService.trackClickOut({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    logContext: post.logContext,
                    mediaId: params.mediaId,
                    clickoutUrl: post.clickouts[0].url,
                    eventSource,
                    target: ClickoutTarget.BUTTON,
                    isBoosted: Boolean(post.adsData),
                    collectionId: fromCollectionId,
                    businessId: post.business?.id,
                    columns,
                });
                ymTrackClickout({ own: post.ownersData?.isOwner });
                trackEcommerceEvent({
                    post,
                    trackEcommerceView: true,
                    trackEcommerceAdd: true,
                });
            },
            [post, fromCollectionId, columns],
        );

        const onSlideChange = useCallback(
            (params: OnSlideChangeParams) => {
                analyticsService.trackSlideMoved({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    logContext: post.logContext,
                    mediaId: params.mediaId,
                    eventSource,
                    sliderContentNumber: params.mediaIndex,
                    collectionId: fromCollectionId,
                    columns,
                });
                trackEcommerceEvent({
                    post,
                    trackEcommerceView: true,
                });
            },
            [post, fromCollectionId, columns],
        );

        const onVideoEnd = useCallback(
            (params: OnVideoStateChangeParams) => {
                analyticsService.trackVideoStateChange({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    logContext: post.logContext,
                    eventSource: eventSource,
                    mediaId: params.mediaId,
                    state: {
                        eventType: VideoStates.VIDEO_FINISHED,
                        wasNotTriggeredByUser: true,
                    },
                    columns,
                });
                trackEcommerceEvent({
                    post,
                    trackEcommerceView: true,
                });
            },
            [post, columns],
        );

        const onVideoStart = useCallback(
            (params: OnVideoStateChangeParams) => {
                analyticsService.trackVideoStateChange({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    logContext: post.logContext,
                    eventSource: eventSource,
                    mediaId: params.mediaId,
                    state: {
                        eventType: VideoStates.VIDEO_STARTED,
                    },
                    columns,
                });
            },
            [post, columns],
        );

        const onVideoPlay = useCallback(
            (params: OnVideoStateChangeParams) => {
                if (EventSource.POST === eventSource) {
                    ymTrackPostInGridVideoPlay({
                        page: getPageNameByEventSource(eventSource),
                        userLogin: currentUser?.login,
                        collectionId: fromCollectionId,
                    });
                }
                analyticsService.trackVideoStateChange({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    logContext: post.logContext,
                    eventSource: eventSource,
                    mediaId: params.mediaId,
                    state: {
                        eventType: VideoStates.VIDEO_RESUMED,
                    },
                    columns,
                });
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [post, eventSource, fromCollectionId, currentUser, columns],
        );

        const onVideoPause = useCallback(
            (params: OnVideoStateChangeParams) => {
                analyticsService.trackVideoStateChange({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    logContext: post.logContext,
                    eventSource: eventSource,
                    mediaId: params.mediaId,
                    state: {
                        eventType: VideoStates.VIDEO_PAUSED,
                    },
                    columns,
                });
            },
            [post, columns],
        );

        const onExpand = useCallback(
            (params: OnExpandParams) => {
                other.onExpand?.();
                analyticsService.trackPostExpanded({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    logContext: post.logContext,
                    mediaId: params.mediaId,
                    sliderContentNumber: params.mediaIndex,
                    eventSource,
                    collectionId: fromCollectionId,
                    columns,
                });
                ymTrackPostExpand({ own: post.ownersData?.isOwner });
                trackEcommerceEvent({
                    post,
                    trackEcommerceView: true,
                });
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [post, fromCollectionId, columns],
        );

        const onVisibilityChange = useCallback(
            (params: OnVisibilityChangeParams) => {
                analyticsService.trackPostVisible({
                    ...params,
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    logContext: post.logContext,
                    eventSource,
                    collectionId: fromCollectionId,
                    products: post.products.items.map((product) => product.id),
                    isBoosted: Boolean(post.adsData),
                    columns,
                });

                // В посте grid, там другая логика скролла
                if (params.postNumber >= 1 && eventSource !== EventSource.POST) {
                    ymTrackFeedScroll({ own: post.ownersData?.isOwner });
                }
                if (params.postNumber === 4 && params.isInViewport) {
                    ymTrackSawFifthClick();
                }
                if (params.postNumber === 9 && params.isInViewport) {
                    ymTrackSawTenthClick();
                }
                if (params.postNumber === 14 && params.isInViewport) {
                    ymTrackSawFifteenthClick();
                }

                if (post.adsData?.showUrl && params.isInViewport) {
                    trackViewPostBoost(post.adsData.showUrl);
                }
            },
            [post, fromCollectionId, columns],
        );

        const onVideoMuteChange = useCallback(
            (params: OnMuteChangeParams) => {
                analyticsService.trackVideoStateChange({
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    logContext: post.logContext,
                    eventSource,
                    mediaId: params.mediaId,
                    collectionId: fromCollectionId,
                    state: {
                        eventType: params.mute
                            ? VideoStates.VIDEO_MUTED
                            : VideoStates.VIDEO_UN_MUTED,
                    },
                    columns,
                });
            },
            [post, fromCollectionId, columns],
        );

        const onRemove = useCallback(() => {
            ymTrackPostDeleteClick({
                page: getPageNameByEventSource(eventSource),
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });

            const isCollection = post.postType === 'collection';
            confirmAlert(
                'Удаление',
                `Вы действительно хотите безвозвратно удалить ${
                    isCollection ? 'коллекцию' : 'пост'
                }?`,
            )
                .then(() => {
                    if (isCollection) {
                        dispatch(removeCollectionAsync(post.id));
                    }

                    return dispatch(removePostsAsync(post.id));
                })
                .then(() => {
                    let path = '/';

                    if (fromCollectionId) {
                        path = `/collections/${fromCollectionId}`;
                    } else if (fromBusinessId) {
                        const managementPath = fromManagement ? '/management' : '';

                        if (isBloggerBusiness(post.business?.type)) {
                            path = `/profile/${fromBusinessId}${managementPath}`;
                        } else {
                            path = `/businesses/${fromBusinessId}${managementPath}`;
                        }
                    }

                    const lastUrl = History.getLast();
                    const previousUrl = History.getPrevious();

                    if (lastUrl === path) {
                        // уже находимся на нужной странице
                    } else if (previousUrl === path) {
                        // предыдущая страница является нужной
                        navigate(-1);
                    } else {
                        // реплейсим стейт, чтобы нельзя было вернуться на удаленный пост
                        navigate(path, { replace: true });
                    }
                    other.onRemove?.();
                })
                .catch(noop);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [post.id, currentUser?.login, currentUser?.profile?.publicId, eventSource, dispatch]);

        const onEdit = useCallback(() => {
            ymTrackPostEditClick({
                page: getPageNameByEventSource(eventSource),
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            navigate(`/posts/${post.id}/edit`);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [post, currentUser?.login, currentUser?.profile?.publicId, eventSource, navigate]);

        const makeUpdateStatusHandler = useCallback(
            (status: PostStatus) => {
                if (postsService.canChangeStatus(post, status, currentUser)) {
                    return () => dispatch(updatePostStatusAsync([post.id], status, currentUser));
                }

                return undefined;
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [currentUser?.role, post.status],
        );

        const onProductButtonClick = useCallback(() => {
            analyticsService.trackProductsUnroll({
                products: post.products.items.map((product) => product.id),
                businessId: post.business?.id,
                postId: post.id,
                postNumber: index,
                djRequestId: post.djRequestId,
                logContext: post.logContext,
                eventSource,
                collectionId: fromCollectionId,
                columns,
            });
            ymTrackProductsUnroll({
                page: getPageNameByEventSource(eventSource),
                userLogin: currentUser?.login,
            });
            trackEcommerceEvent({
                post,
                trackEcommerceView: true,
            });
            openDialog(DIALOGS.POST_PRODUCTS, { post, eventSource });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [openDialog, post, eventSource, currentUser?.login, index, fromCollectionId, columns]);

        const onContextMenuOpen = useCallback(() => {
            if (EventSource.POST === eventSource) {
                ymTrackPostInGridKebabClick({
                    page: getPageNameByEventSource(eventSource),
                    userLogin: currentUser?.login,
                    collectionId: fromCollectionId,
                });
            } else {
                ymTrackContextMenuOpen({
                    page: getPageNameByEventSource(eventSource),
                    userLogin: currentUser?.login,
                    collectionId: fromCollectionId,
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [eventSource, fromCollectionId]);

        const moveToCollection = useCallback(() => {
            navigate(`/collections/${post.collectionData?.id}`);

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [navigate, post.collectionData?.id, post.djRequestId, post.id, post.logContext]);

        const onCollectionGridCardClick = useCallback(
            (params: OnGridCardClickParams) => {
                analyticsService.trackGridTap({
                    eventSource,
                    collectionId: post.collectionData?.id,
                    postId: post.id,
                    postNumber: params.postNumber,
                    logContext: post.logContext,
                    djRequestId: post.djRequestId,
                    columns,
                });
                moveToCollection();
            },
            [
                moveToCollection,
                post.collectionData?.id,
                post.djRequestId,
                post.id,
                post.logContext,
                columns,
            ],
        );

        const onEditPostCollection = useCallback(() => {
            openDialog(DIALOGS.EDIT_COLLECTION, { collectionId: post.collectionData?.id });
            contentActionsService.trackCollectionEditDialogOpen({
                id: post.collectionData?.id ?? '',
                screen: eventSource ?? EventSource.COLLECTION,
            });
        }, [post.collectionData?.id, openDialog]);

        const onStatusBadgeClick = useCallback(() => {
            ymTrackPostStatusClick({
                page: getPageNameByEventSource(eventSource),
                userLogin: currentUser?.login,
                value: post.status,
            });

            openDialog(DIALOGS.POST_STATUS, { post });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [post, openDialog]);

        const handleBusinessPage = useCallback(
            (isKebab: boolean) => {
                navigate(`/businesses/${post.business?.publicId}`);

                if (isKebab) {
                    ymTrackPostInGridKebabBusinessClick({
                        page: getPageNameByEventSource(eventSource),
                        userLogin: currentUser?.login,
                        collectionId: fromCollectionId,
                    });
                } else {
                    ymTrackPostInGridBusinessClick({
                        page: getPageNameByEventSource(eventSource),
                        userLogin: currentUser?.login,
                        businessId: post.business?.publicId,
                    });
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [post.business?.id, navigate, currentUser],
        );

        const onBusinessPage = post.business?.publicId ? handleBusinessPage : undefined;

        const onGridRowShown = useCallback(() => {
            ymTrackPostInGridRowShown({
                page: getPageNameByEventSource(eventSource),
                userLogin: currentUser?.login,
                collectionId: fromCollectionId,
            });

            ymTrackPostInGridScroll({
                page: getPageNameByEventSource(eventSource),
                userLogin: currentUser?.login,
                collectionId: fromCollectionId,
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [eventSource, fromCollectionId, currentUser]);

        // Только для гридовых постов
        const onHover = useCallback(() => {
            sendPostViewEvent();
            analyticsService.gridPostHover({
                postId: post.id,
                eventSource: eventSource,
                businessId: post.business?.id,
                djRequestId: post.djRequestId,
                logContext: post.logContext,
                columns,
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [eventSource, post, columns, sendPostViewEvent]);

        // Только для гридовых постов
        const onHoverCancel = useCallback(() => {
            analyticsService.gridPostHoverCancel({
                postId: post.id,
                eventSource: eventSource,
                businessId: post.business?.id,
                djRequestId: post.djRequestId,
                logContext: post.logContext,
                columns,
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [eventSource, post, columns]);

        const onGridCardClick = useCallback(
            (params: OnGridCardClickParams) => {
                const gridClickout = post.clickouts?.find(
                    (clickout) => clickout.target === ClickoutTarget.GRID,
                );
                const page = getPageNameByEventSource(eventSource);

                if (gridClickout) {
                    const url = gridClickout.url;

                    ymTrackClickout({ own: post.ownersData?.isOwner });

                    analyticsService.trackClickOut({
                        postId: post.id,
                        djRequestId: post.djRequestId,
                        logContext: post.logContext,
                        clickoutUrl: url,
                        eventSource,
                        target: ClickoutTarget.GRID,
                        isBoosted: Boolean(post.adsData),
                        collectionId: fromCollectionId,
                        businessId: post.business?.id,
                        columns,
                    });

                    window.open(url, '_blank');
                } else {
                    ymTrackPostInGridClick({
                        page,
                        userLogin: currentUser?.login,
                        businessId: currentUser?.profile?.publicId,
                    });
                    analyticsService.trackGridTap({
                        eventSource,
                        postId: post.id,
                        postNumber: params.postNumber,
                        logContext: post.logContext,
                        djRequestId: post.djRequestId,
                        columns,
                    });
                    navigate(post.url);
                }

                if (eventSource === EventSource.SEARCH) {
                    ymTrackSearchGoToPost({
                        page,
                        userLogin: currentUser?.login,
                        businessId: currentUser?.profile?.publicId,
                    });
                }

                sendPostViewEvent();
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [
                currentUser?.login,
                currentUser?.profile?.publicId,
                navigate,
                post.id,
                post.logContext,
                post.url,
                columns,
                sendPostViewEvent,
            ],
        );

        const onPostTextProductsClick = useCallback(
            (product: ProductItemUI) => {
                analyticsService.trackClickOut({
                    clickoutUrl: product.url,
                    eventSource: EventSource.POST,
                    productId: product.id,
                    businessId: post.business?.id,
                    target: 'product',
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    logContext: post.logContext,
                    isBoosted: Boolean(post.adsData),
                    collectionId: fromCollectionId,
                    columns,
                });

                ymTrackProductClickOut({
                    page: 'Страница поста',
                    userLogin: currentUser?.login,
                    businessId: currentUser?.profile?.publicId,
                });
                trackEcommerceEvent({
                    post,
                    trackEcommerceView: true,
                });
            },
            [post, fromCollectionId, currentUser?.login, currentUser?.profile?.publicId, columns],
        );

        const onPostTextCheckoutClick = useCallback(
            (product: ProductItemUI) => {
                analyticsService.trackClickOut({
                    clickoutUrl: product.checkout?.url,
                    eventSource: EventSource.POST,
                    productId: product.id,
                    businessId: post.business?.id,
                    target: 'product_checkout',
                    postId: post.id,
                    djRequestId: post.djRequestId,
                    logContext: post.logContext,
                    isBoosted: Boolean(post.adsData),
                    collectionId: fromCollectionId,
                    columns,
                });

                ymTrackProductClickOut({
                    page: 'Страница поста',
                    userLogin: currentUser?.login,
                    businessId: currentUser?.profile?.publicId,
                });
                trackEcommerceEvent({
                    post,
                    trackEcommerceView: true,
                });
            },
            [post, fromCollectionId, currentUser?.login, currentUser?.profile?.publicId, columns],
        );

        const onPostTextCatalogClick = useCallback(() => {
            analyticsService.trackOpenCatalog({
                businessId: post.business?.id,
                eventSource: EventSource.POST,
                columns,
            });
            ymTrackOpenCatalogFromPost({
                page: 'Страница поста',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
        }, [currentUser?.login, currentUser?.profile?.publicId, post, columns]);

        const onPostTextClickoutClick = useCallback(() => {
            const clickOut = (post.clickouts ?? []).find((c) => c.target === 'button' && c.url);

            analyticsService.trackClickOut({
                eventSource: EventSource.POST,
                businessId: post.business?.id,
                postId: post.id,
                clickoutUrl: clickOut?.url,
                target: ClickoutTarget.BUTTON,
                djRequestId: post.djRequestId,
                logContext: post.logContext,
                isBoosted: Boolean(post.adsData),
                collectionId: fromCollectionId,
                columns,
            });
            ymTrackClickout({
                page: 'Страница поста',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
                own: post.ownersData?.isOwner,
            });
            trackEcommerceEvent({
                post,
                trackEcommerceView: true,
            });
        }, [currentUser?.login, currentUser?.profile?.publicId, fromCollectionId, post, columns]);

        return (
            // @ts-ignore
            <BaseComponent
                {...other}
                innerRef={innerRef}
                gridColumns={columns}
                index={index}
                post={toPostUI(post)}
                onEdit={canEditBusiness(post.business?.id, currentUser) ? onEdit : undefined}
                onDraft={makeUpdateStatusHandler(PostStatus.DRAFT)}
                onPublish={makeUpdateStatusHandler(PostStatus.MODERATION)}
                onRemove={postsService.canRemove(post, currentUser) ? onRemove : undefined}
                onShare={onShare}
                onShareCollection={onShareCollection}
                onSaveCollection={onSaveCollection}
                onRemoveSavedCollection={onRemoveSavedCollection}
                onReport={other.onReport || onReport}
                onReaction={onReaction}
                onSubscribe={onSubscribe}
                onSlideChange={onSlideChange}
                onBookmark={onBookmark}
                onClickOutClick={onClickOutClick}
                onVisibilityChange={onVisibilityChange}
                onExpand={onExpand}
                onVideoMuteChange={onVideoMuteChange}
                onVideoEnd={onVideoEnd}
                onVideoStart={onVideoStart}
                onVideoPlay={onVideoPlay}
                onVideoPause={onVideoPause}
                onProductButtonClick={onProductButtonClick}
                onPostTextProductsClick={onPostTextProductsClick}
                onPostTextCheckoutClick={onPostTextCheckoutClick}
                onPostTextCatalogClick={onPostTextCatalogClick}
                onPostTextClickoutClick={onPostTextClickoutClick}
                onStatusBadgeClick={onStatusBadgeClick}
                onTriggerClick={onContextMenuOpen}
                onBusinessPage={onBusinessPage}
                onGridRowShown={onGridRowShown}
                moveToCollection={moveToCollection}
                onEditPostCollection={onEditPostCollection}
                onHover={onHover}
                onHoverCancel={onHoverCancel}
                onGridCardClick={onGridCardClick}
                onCollectionGridCardClick={onCollectionGridCardClick}
            />
        );
    };
